import { useRef, useState, useCallback } from 'react';
import { usePhotoUpload } from '../hooks/usePhotoUpload';
import { PhotoPreviewCard } from './PhotoPreviewCard';
import { PhotoAnnotationModal } from './PhotoAnnotationModal';
import { UploadStatusModal } from '@/components/ui';

interface PhotoUploaderProps {
  onComplete?: () => void;
  projects?: Array<{ id: string; name: string }>;
  defaultProjectId?: string | null;
}

export function PhotoUploader({
  onComplete,
  projects = [],
  defaultProjectId,
}: PhotoUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [annotatingPhotoId, setAnnotatingPhotoId] = useState<string | null>(null);

  // Upload status modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    status: 'uploading' | 'success' | 'error';
    totalCount: number;
    successCount: number;
    errorCount: number;
  }>({
    isOpen: false,
    status: 'uploading',
    totalCount: 0,
    successCount: 0,
    errorCount: 0,
  });

  const handleUploadStart = useCallback((count: number) => {
    setModalState({
      isOpen: true,
      status: 'uploading',
      totalCount: count,
      successCount: 0,
      errorCount: 0,
    });
  }, []);

  const handleUploadComplete = useCallback((successCount: number, errorCount: number, totalCount: number) => {
    setModalState({
      isOpen: true,
      status: errorCount > 0 ? 'error' : 'success',
      totalCount,
      successCount,
      errorCount,
    });
  }, []);

  const {
    photos,
    isProcessing,
    addFiles,
    removePhoto,
    setSelectedProject,
    setDescription,
    uploadPhotos,
    clearAll,
  } = usePhotoUpload({
    defaultProjectId,
    onUploadStart: handleUploadStart,
    onUploadComplete: handleUploadComplete,
  });

  const handleModalClose = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
    // If uploads were successful, call onComplete and clear the list
    if (modalState.status === 'success') {
      onComplete?.();
      clearAll();
    }
  }, [modalState.status, onComplete, clearAll]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(e.target.files);
    }
    // Reset input so the same file can be selected again
    e.target.value = '';
  }, [addFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      addFiles(files);
    }
  }, [addFiles]);

  const handleAnnotate = useCallback((photoId: string) => {
    setAnnotatingPhotoId(photoId);
  }, []);

  const handleAnnotationSave = useCallback((description: string) => {
    if (annotatingPhotoId) {
      setDescription(annotatingPhotoId, description);
    }
  }, [annotatingPhotoId, setDescription]);

  const handleAnnotationClose = useCallback(() => {
    setAnnotatingPhotoId(null);
  }, []);

  const annotatingPhoto = annotatingPhotoId
    ? photos.find((p) => p.id === annotatingPhotoId)
    : null;

  const readyCount = photos.filter((p) => p.status === 'ready').length;
  const uploadedCount = photos.filter((p) => p.status === 'uploaded').length;
  const hasErrors = photos.some((p) => p.status === 'error');

  // Hide project select when defaultProjectId is provided
  const showProjectSelect = !defaultProjectId;

  return (
    <>
      <div className="space-y-6">
        {/* Drop Zone */}
        <div
          className={`
            border-2 border-dashed rounded-lg p-8 text-center transition-colors
            ${isDragging
              ? 'border-primary-500 bg-primary-50'
              : 'border-secondary-300'
            }
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
            capture="environment"
            className="hidden"
            onChange={handleFileSelect}
          />

          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <p className="text-secondary-900 font-medium">
                Add photos
              </p>
              <p className="text-secondary-500 text-sm mt-1">
                JPEG, PNG, HEIC up to 25MB
                {showProjectSelect && ' • GPS data will be used to suggest projects'}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Take Photo
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-secondary-700 border border-secondary-300 rounded-lg font-medium hover:bg-secondary-50 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                Browse Photos
              </button>
            </div>

            <p className="text-secondary-400 text-xs hidden sm:block">
              or drag and drop photos here
            </p>
          </div>
        </div>

        {/* Photo Grid */}
        {photos.length > 0 && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-secondary-600">
                {photos.length} photo{photos.length !== 1 ? 's' : ''} selected
                {uploadedCount > 0 && ` • ${uploadedCount} uploaded`}
              </p>
              <button
                onClick={clearAll}
                className="text-sm text-secondary-500 hover:text-secondary-700"
                disabled={isProcessing}
              >
                Clear all
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {photos.map((photo) => (
                <PhotoPreviewCard
                  key={photo.id}
                  photo={photo}
                  onRemove={removePhoto}
                  onProjectChange={setSelectedProject}
                  onAnnotate={handleAnnotate}
                  projects={projects}
                  showProjectSelect={showProjectSelect}
                />
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              {hasErrors && (
                <p className="text-sm text-red-600 self-center">
                  Some photos failed to process
                </p>
              )}
              <button
                onClick={uploadPhotos}
                disabled={readyCount === 0 || isProcessing}
                className={`
                  px-4 py-2 rounded-lg font-medium transition-colors
                  ${readyCount === 0 || isProcessing
                    ? 'bg-secondary-100 text-secondary-400 cursor-not-allowed'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                  }
                `}
              >
                Upload {readyCount} Photo{readyCount !== 1 ? 's' : ''}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Upload Status Modal */}
      <UploadStatusModal
        isOpen={modalState.isOpen}
        status={modalState.status}
        totalCount={modalState.totalCount}
        successCount={modalState.successCount}
        errorCount={modalState.errorCount}
        onClose={handleModalClose}
        itemType="photo"
      />

      {/* Photo Annotation Modal */}
      <PhotoAnnotationModal
        isOpen={annotatingPhotoId !== null}
        onClose={handleAnnotationClose}
        onSave={handleAnnotationSave}
        initialValue={annotatingPhoto?.description || ''}
        photoName={annotatingPhoto?.file.name || ''}
      />
    </>
  );
}